/**
 * SessionNotesScreen — capture session notes + per-goal trial data
 * + export the parent hand-off PDF (PRD §3 #5 + #6).
 *
 * v1.0-C2 wires the full notes capture flow:
 *   - Session header (student name + completed_at).
 *   - "Session Notes" textarea — captures clinician's free-text
 *     notes.
 *   - Per-goal data capture: for each goal that participated in
 *     this session, trials + correct + cueing_level. New
 *     session_data_points rows are INSERTed on Save.
 *   - "Save Notes" → UPDATE sessions SET notes_text = <text>,
 *     completed_at = now() (idempotent — won't overwrite if
 *     already set).
 *   - "Auto-update goal accuracy" → for each goal with new data
 *     in this session, recompute a rolling average and UPDATE
 *     goals.current_accuracy.
 *   - "Export Parent Hand-off" button → calls
 *     `api-export-parent-handoff` → receives
 *     `{ pdf_base64, filename }` → triggers a browser download
 *     (web preview) or, on the native iOS build handled via
 *     `SessionPDFShare` + `UIActivityViewController`.
 *   - Free-tier gate: if ai_consent_granted_at is null or stale,
 *     the Export button is disabled with the standard "Enable
 *     AI features in onboarding first" message.
 *
 * Conventions mirror SessionPlannerScreen.tsx + SessionFormScreen.tsx
 * (PRD §3 #3 surface):
 *   * `: ReactElement` return type.
 *   * Tailwind via the existing class names.
 *   * `void (async () => { ... })()` for fire-and-forget promises.
 *   * Cancellation guard on every async effect.
 */
import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../lib/supabase/client';
import { formatShortDate } from '../lib/effectiveslp/domain';
import { PracticeLinkComposer } from './student/PracticeLinkComposer';

interface Props {
  sessionId: string;
  onBack: () => void;
}

interface SessionRow {
  id: string;
  student_id: string;
  scheduled_at: string;
  completed_at: string | null;
  status: string;
  notes_text: string | null;
}

interface StudentRow {
  id: string;
  first_name: string;
  age_years: number | null;
}

interface GoalRow {
  id: string;
  area: string;
  goal_text: string;
  target_accuracy: number | null;
  current_accuracy: number | null;
}

interface UserEntitlementRow {
  ai_consent_granted_at: string | null;
  students_enabled: number;
}

interface GoalDataCapture {
  goal_id: string;
  trials: number;
  correct: number;
  cueing_level: 'independent' | 'verbal' | 'visual' | 'tactile' | 'physical' | '';
}

interface ExportEnvelope {
  pdf_base64: string;
  filename: string;
  log_id: string | null;
  model_used?: string;
  /** Optional structured payload from the Worker when available. */
  model_used_full?: {
    family: string;
    model: string;
    vendor: string;
  };
}

const AI_CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

function isConsentFresh(iso: string | null): boolean {
  if (!iso) return false;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return false;
  return Date.now() - ms <= AI_CONSENT_MAX_AGE_MS;
}

export function SessionNotesScreen({
  sessionId,
  onBack,
}: Props): ReactElement {
  const [session, setSession] = useState<SessionRow | null>(null);
  const [student, setStudent] = useState<StudentRow | null>(null);
  const [goals, setGoals] = useState<GoalRow[]>([]);
  const [entitlement, setEntitlement] = useState<UserEntitlementRow | null>(null);
  const [notesText, setNotesText] = useState('');
  const [dataCapture, setDataCapture] = useState<Record<string, GoalDataCapture>>(
    {},
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [updatingAccuracy, setUpdatingAccuracy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState<string | null>(null);
  const [composerOpen, setComposerOpen] = useState(false);

  // ----- Initial load -----
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data: sRow, error: sErr } = await supabase
          .from('sessions')
          .select(
            'id, student_id, scheduled_at, completed_at, status, notes_text',
          )
          .eq('id', sessionId)
          .maybeSingle();
        if (cancelled) return;
        if (sErr || !sRow) {
          setError(sErr?.message ?? 'Session not found.');
          return;
        }
        const sess = sRow as SessionRow;
        setSession(sess);
        setNotesText(sess.notes_text ?? '');

        const [stuRes, goalsRes] = await Promise.all([
          supabase
            .from('students')
            .select('id, first_name, age_years')
            .eq('id', sess.student_id)
            .maybeSingle(),
          supabase
            .from('goals')
            .select(
              'id, area, goal_text, target_accuracy, current_accuracy',
            )
            .eq('student_id', sess.student_id)
            .eq('status', 'active')
            .order('created_at'),
        ]);
        if (cancelled) return;
        const stu = stuRes.data as StudentRow | null;
        setStudent(stu);
        const gs = (goalsRes.data as GoalRow[] | null) ?? [];
        setGoals(gs);
        // Seed a default GoalDataCapture row per goal so the
        // per-goal UI is rendered immediately.
        const seed: Record<string, GoalDataCapture> = {};
        for (const g of gs) {
          seed[g.id] = {
            goal_id: g.id,
            trials: 0,
            correct: 0,
            cueing_level: '',
          };
        }
        setDataCapture(seed);

        // Read the clinician's AI consent + Pro state from the
        // session (so we can gate the Export button).
        const { data: sessionData } = await supabase.auth.getSession();
        const uid = sessionData.session?.user?.id;
        if (uid) {
          const { data: entRow } = await supabase
            .from('users')
            .select('ai_consent_granted_at, students_enabled')
            .eq('id', uid)
            .maybeSingle();
          if (!cancelled) {
            setEntitlement((entRow as UserEntitlementRow | null) ?? null);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : 'Failed to load session.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (!session && !error) {
    return (
      <main className="screen">
        <p className="tagline">Loading session…</p>
      </main>
    );
  }
  if (error) {
    return (
      <main className="screen">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back
        </button>
        <p className="error">{error}</p>
      </main>
    );
  }

  const consentFresh = isConsentFresh(
    entitlement?.ai_consent_granted_at ?? null,
  );

  function setCaptureField<K extends keyof GoalDataCapture>(
    goalId: string,
    field: K,
    value: GoalDataCapture[K],
  ): void {
    setDataCapture((prev) => {
      const cur = prev[goalId] ?? {
        goal_id: goalId,
        trials: 0,
        correct: 0,
        cueing_level: '' as const,
      };
      return { ...prev, [goalId]: { ...cur, [field]: value } };
    });
  }

  async function handleSaveNotes(): Promise<void> {
    if (!session) return;
    setSaving(true);
    setError(null);
    try {
      const updates: Record<string, unknown> = {
        notes_text: notesText,
      };
      // First save — set completed_at to now if it's null.
      if (session.completed_at == null) {
        updates.completed_at = new Date().toISOString();
        updates.status = 'completed';
      }
      const { error: uErr } = await supabase
        .from('sessions')
        .update(updates)
        .eq('id', sessionId);
      if (uErr) {
        setError(uErr.message);
        return;
      }
      // Reflect the change locally so the Save button shows the
      // "completed" state without a roundtrip.
      setSession({
        ...session,
        notes_text: notesText,
        completed_at: session.completed_at ?? (updates.completed_at as string),
        status: session.completed_at == null ? 'completed' : session.status,
      });

      // Replace (not append) this session's session_data_points rows.
      // Capture is idempotent per session — re-saving or double-tapping
      // Save must not duplicate rows for the same (session_id, goal_id),
      // which would otherwise double-count into the rolling-average
      // window read by handleUpdateAccuracy below
      // (fix #130: web Save Notes re-inserts duplicates).
      //
      // fix #148: do this via the replace_session_data_points RPC so
      // the DELETE and INSERT are a single Postgres transaction. A
      // failed insert after a successful delete would otherwise
      // silently lose the session's prior captures.
      const newRows = Object.values(dataCapture)
        .filter((c) => c.trials > 0 && c.correct >= 0 && c.correct <= c.trials)
        .map((c) => ({
          goal_id: c.goal_id,
          trials: c.trials,
          correct: c.correct,
          cueing_level: c.cueing_level.length > 0 ? c.cueing_level : null,
        }));
      // The RPC deletes prior rows for this session (owned by the
      // caller; rejected with `not_session_owner` otherwise) and
      // inserts the new payload in one transaction. Empty payload
      // is allowed — it clears the session's captures, e.g. when
      // the user reduced every goal to trials=0.
      const { error: rpcErr } = await supabase.rpc(
        'replace_session_data_points',
        {
          p_session_id: sessionId,
          p_rows: newRows,
        },
      );
      if (rpcErr) {
        // Don't roll back the notes save on data-point failure;
        // surface as a non-fatal message.
        setError(
          `Notes saved, but data-point reset failed: ${rpcErr.message}`,
        );
        return;
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateAccuracy(): Promise<void> {
    if (!session) return;
    setUpdatingAccuracy(true);
    setError(null);
    try {
      // Compute a rolling average per goal from session_data_points
      // (last 5 data points) and UPDATE goals.current_accuracy.
      for (const g of goals) {
        const { data: dps, error: dpErr } = await supabase
          .from('session_data_points')
          .select('trials, correct')
          .eq('goal_id', g.id)
          .order('created_at', { ascending: false })
          .limit(5);
        if (dpErr) continue;
        const rows = (dps ?? []) as Array<{ trials: number; correct: number }>;
        const totalTrials = rows.reduce((acc, r) => acc + r.trials, 0);
        const totalCorrect = rows.reduce((acc, r) => acc + r.correct, 0);
        if (totalTrials === 0) continue;
        const avg = Math.round((totalCorrect / totalTrials) * 100);
        const { error: uErr } = await supabase
          .from('goals')
          .update({ current_accuracy: avg })
          .eq('id', g.id);
        if (!uErr) {
          setGoals((prev) =>
            prev.map((gg) =>
              gg.id === g.id ? { ...gg, current_accuracy: avg } : gg,
            ),
          );
        }
      }
    } finally {
      setUpdatingAccuracy(false);
    }
  }

  async function handleExport(): Promise<void> {
    if (!consentFresh || !session) return;
    setExporting(true);
    setError(null);
    setExportMessage(null);
    try {
      // 1) Persist notes first so the PDF (with include_notes=true)
      //    picks up the latest notes_text.
      if ((session.notes_text ?? '') !== notesText) {
        const { error: saveErr } = await supabase
          .from('sessions')
          .update({ notes_text: notesText })
          .eq('id', sessionId);
        if (saveErr) {
          setError(saveErr.message);
          return;
        }
      }
      // 2) Invoke the export Edge Function.
      const { data, error: invErr } = await supabase.functions.invoke<ExportEnvelope>(
        'api-export-parent-handoff',
        {
          body: {
            session_id: sessionId,
            include_notes: true,
          },
        },
      );
      if (invErr) {
        setError(invErr.message);
        return;
      }
      if (!data) {
        setError('Empty response from parent-handoff exporter.');
        return;
      }
      // 3) Deliver the PDF to the user. The native iOS app
      //    replaced the original React Capacitor bridge with
      //    SessionPDFShare + UIActivityViewController
      //    (ios/EffectiveSLP/Session/SessionPDFShare.swift) so
      //    this React code path only runs in web-preview builds,
      //    where a browser download is the right answer.
      const bytes = base64ToUint8(data.pdf_base64);
      // Hand the bytes to the browser as a data: URL. We already
      // hold a base64 string from the Edge Function; this avoids
      // the TS-strict-mode Blob/Uint8Array<ArrayBufferLike>
      // shape mismatch and the round-trip through BlobPart.
      const dataUrl = `data:application/pdf;base64,${data.pdf_base64}`;
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setExportMessage(
        `Exported ${data.filename} (${bytes.length} bytes).`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <main className="screen">
      <header className="screen-header">
        <button type="button" className="link-button" onClick={onBack}>
          ← Back
        </button>
        <h1>Session notes</h1>
        <p className="tagline">
          {student?.first_name ?? 'Student'}
          {student?.age_years != null ? ` · Age ${student.age_years}` : ''}
          {' · '}
          {formatShortDate(session!.scheduled_at)}
          {session!.completed_at ? ' · Completed' : ''}
        </p>
      </header>

      <section>
        <h2 className="onboarding-eyebrow">Session notes</h2>
        <textarea
          value={notesText}
          onChange={(e) => setNotesText(e.target.value)}
          rows={5}
          placeholder="What happened in this session? Trial observations, parent updates, anything to remember next time…"
        />
      </section>

      {goals.length > 0 && (
        <section>
          <h2 className="onboarding-eyebrow">Per-goal data</h2>
          {goals.map((g) => {
            const cap = dataCapture[g.id];
            return (
              <div key={g.id} className="card stack-md">
                <span className="caseload-row-name">
                  {g.area} — {g.goal_text}
                </span>
                <div className="form-row">
                  <label>
                    Trials
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={cap?.trials ?? 0}
                      onChange={(e) =>
                        setCaptureField(
                          g.id,
                          'trials',
                          Number.isFinite(Number(e.target.value))
                            ? Number(e.target.value)
                            : 0,
                        )
                      }
                    />
                  </label>
                  <label>
                    Correct
                    <input
                      type="number"
                      min={0}
                      max={cap?.trials ?? 0}
                      value={cap?.correct ?? 0}
                      onChange={(e) =>
                        setCaptureField(
                          g.id,
                          'correct',
                          Number.isFinite(Number(e.target.value))
                            ? Number(e.target.value)
                            : 0,
                        )
                      }
                    />
                  </label>
                  <label>
                    Cueing
                    <select
                      value={cap?.cueing_level ?? ''}
                      onChange={(e) =>
                        setCaptureField(
                          g.id,
                          'cueing_level',
                          e.target.value as GoalDataCapture['cueing_level'],
                        )
                      }
                    >
                      <option value="">—</option>
                      <option value="independent">Independent</option>
                      <option value="verbal">Verbal</option>
                      <option value="visual">Visual</option>
                      <option value="tactile">Tactile</option>
                      <option value="physical">Physical</option>
                    </select>
                  </label>
                </div>
              </div>
            );
          })}
        </section>
      )}

      <section className="form-actions">
        <button
          type="button"
          className="snap-button"
          disabled={saving}
          onClick={() => void handleSaveNotes()}
        >
          {saving ? 'Saving…' : 'Save Notes'}
        </button>
        <button
          type="button"
          className="snap-button"
          disabled={updatingAccuracy || goals.length === 0}
          onClick={() => void handleUpdateAccuracy()}
        >
          {updatingAccuracy ? 'Updating…' : 'Auto-update goal accuracy'}
        </button>
        <button
          type="button"
          className="snap-button"
          disabled={exporting || !consentFresh}
          onClick={() => void handleExport()}
        >
          {exporting
            ? 'Exporting…'
            : consentFresh
              ? 'Export Parent Hand-off'
              : 'Enable AI features in onboarding first'}
        </button>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setComposerOpen(true)}
        >
          Send practice link
        </button>
      </section>

      {composerOpen && session && (
        <PracticeLinkComposer
          studentId={session.student_id}
          sessionId={session.id}
          onClose={() => setComposerOpen(false)}
        />
      )}

      {exportMessage && <p className="tagline">{exportMessage}</p>}
      {error && <p className="error">{error}</p>}
    </main>
  );
}

/**
 * Decode a base64 string to a Uint8Array. Mirrors the encoder in
 * the Edge Function so the round-trip is byte-exact.
 */
function base64ToUint8(b64: string): Uint8Array {
  const ALPHABET =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  const lookup = new Map<string, number>();
  for (let i = 0; i < ALPHABET.length; i++) {
    lookup.set(ALPHABET[i]!, i);
  }
  // Strip padding.
  const clean = b64.replace(/=+$/, '');
  const outLen = Math.floor((clean.length * 6) / 8);
  const out = new Uint8Array(outLen);
  let bi = 0;
  for (let i = 0; i < clean.length; i += 4) {
    const c0 = lookup.get(clean[i] ?? 'A') ?? 0;
    const c1 = lookup.get(clean[i + 1] ?? 'A') ?? 0;
    const c2 = i + 2 < clean.length ? lookup.get(clean[i + 2] ?? 'A') ?? 0 : 0;
    const c3 = i + 3 < clean.length ? lookup.get(clean[i + 3] ?? 'A') ?? 0 : 0;
    const triple = (c0 << 18) | (c1 << 12) | (c2 << 6) | c3;
    if (bi < outLen) out[bi++] = (triple >> 16) & 0xff;
    if (bi < outLen) out[bi++] = (triple >> 8) & 0xff;
    if (bi < outLen) out[bi++] = triple & 0xff;
  }
  return out;
}